import * as Sentry from '@sentry/react';

/**
 * 오류 신고 및 모니터링 창구 모듈
 * 
 * 전역 런타임 오류 및 React ErrorBoundary에서 포착된 오류를 한곳으로 모아 처리하며,
 * 운영 환경(PROD)에서는 Sentry 원격 로깅을 수행합니다.
 */

let isGlobalHandlersInitialized = false;
let isMonitoringInitialized = false;

/**
 * Sentry 오류 모니터링 초기화 함수
 * 
 * VITE_SENTRY_DSN 환경변수를 확인하여 Sentry SDK를 초기화합니다.
 * DSN이 없거나 미설정된 경우 원격 수집을 비활성화하고 조용히 건너뜁니다.
 * 기본 통합 비활성화 시 번들 크기 이점은 미미한 반면 오류 포착 기능이 손상될 위험이 있어 기본값(통합 활성화)을 유지합니다.
 */
export function initErrorMonitoring() {
  if (typeof window === 'undefined') return;
  if (isMonitoringInitialized) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (!dsn) {
    console.warn('Sentry DSN 미설정 - 오류 원격 수집 비활성');
    return;
  }

  isMonitoringInitialized = true;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    enabled: import.meta.env.PROD,
  });
}

/**
 * 오류 리포트 함수
 * 
 * 콘솔에 [MooWoo 오류] 접두어와 함께 상세 내용을 출력하고,
 * Sentry SDK가 초기화된 경우 원격으로 오류를 전송합니다.
 * 
 * @param {Error|any} error - 발생한 오류 객체 또는 에러 메시지
 * @param {Object} [context={}] - 오류 발생 맥락 { source, extra }
 */
export function reportError(error, context = {}) {
  const source = context.source || 'unknown';
  const extra = context.extra || null;

  // 로컬/브라우저 콘솔 출력 유지
  console.error(`[MooWoo 오류] [${source}]`, error, extra ? { extra } : '');

  // Sentry 원격 전송 (운영 환경 활성화)
  try {
    const errorObject = error instanceof Error
      ? error
      : new Error(typeof error === 'string' ? error : JSON.stringify(error));

    Sentry.captureException(errorObject, {
      tags: {
        source,
      },
      extra: extra || {},
    });
  } catch (sentryErr) {
    console.warn('[MooWoo 오류] Sentry 전송 실패:', sentryErr);
  }
}

/**
 * 메시지/경고 리포트 함수
 * 
 * 예외가 아닌 상태 메시지나 경고를 로컬 콘솔 및 Sentry 원격으로 전송합니다.
 * 
 * @param {string} message - 기록할 메시지
 * @param {Object} [options={}] - { level, extra }
 */
export function reportMessage(message, { level = 'info', extra = {} } = {}) {
  if (level === 'warning' || level === 'error') {
    console.warn(`[MooWoo 메시지] [${level}]`, message, extra ? { extra } : '');
  } else {
    console.log(`[MooWoo 메시지] [${level}]`, message, extra ? { extra } : '');
  }

  try {
    Sentry.captureMessage(message, {
      level,
      extra: extra || {},
    });
  } catch (sentryErr) {
    console.warn('[MooWoo 메시지] Sentry 전송 실패:', sentryErr);
  }
}

/**
 * 전역 에러 핸들러 초기화 함수
 * 
 * window.error 및 window.unhandledrejection 이벤트를 감지하여
 * 처리되지 않은 전역 에러를 reportError 창구로 수집합니다.
 * 중복 등록 방지 플래그를 통해 다중 호출 시에도 리스너가 1회만 등록됩니다.
 */
export function initGlobalErrorHandlers() {
  if (typeof window === 'undefined') return;
  if (isGlobalHandlersInitialized) return;

  isGlobalHandlersInitialized = true;

  // 처리되지 않은 일반 런타임 자바스크립트 오류 처리
  window.addEventListener('error', (event) => {
    reportError(event.error || event.message || '알 수 없는 자바스크립트 오류', {
      source: 'window.error',
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // 처리되지 않은 Promise 거부(비동기 에러) 처리
  window.addEventListener('unhandledrejection', (event) => {
    reportError(event.reason || '처리되지 않은 Promise 거부(unhandledrejection)', {
      source: 'unhandledrejection',
    });
  });
}
